/** Webhook subscriptions, delivery log and redelivery (M15-50–M15-53). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Send } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';
import { JsonBlock, SecretReveal, errorText } from './shared';

interface Sub { id: string; url: string; events: string[]; active: boolean; failureCount: number; disabledAt: string | null; createdAt: string; deliveries: Record<string, number> }
interface Delivery { id: string; eventId: string; eventType: string; status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'DEAD'; attempts: number; lastStatusCode: number | null; lastError: string | null; nextAttemptAt: string; deliveredAt: string | null; createdAt: string }

const DELIVERY_TONE = { PENDING: 'info', SUCCEEDED: 'ok', FAILED: 'warning', DEAD: 'critical' } as const;

function SubForm({ sub, onClose }: { sub?: Sub; onClose: (secret?: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const events = useQuery({ queryKey: ['webhook-events'], queryFn: () => api<string[]>('/webhooks/events') });
  const [url, setUrl] = useState(sub?.url ?? 'https://');
  const [picked, setPicked] = useState<string[]>(sub?.events ?? []);
  const save = useMutation({
    mutationFn: () => (sub ? api(`/webhooks/${sub.id}`, { method: 'PATCH', body: { url, events: picked } }) : api<{ secret: string }>('/webhooks', { method: 'POST', body: { url, events: picked } })),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['webhooks'] }); onClose(sub ? undefined : (r as { secret: string }).secret); },
  });
  return (
    <Modal title={sub ? 'Edit webhook' : 'Add a webhook'} description="CoreX-lite POSTs a small JSON notice; your system then GETs the full record." onClose={() => onClose()}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {save.error && <Alert tone="critical">{errorText(save.error)}</Alert>}
        <Field label="Endpoint" required hint="Public https:// address. Private and internal addresses are refused."><input className={inputClass} type="url" value={url} onChange={(e) => setUrl(e.target.value)} required /></Field>
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">Events <span className="text-red-500">*</span></legend>
          <div className="grid gap-1 sm:grid-cols-2">
            {events.data?.map((ev) => (
              <label key={ev} className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                <input type="checkbox" checked={picked.includes(ev)} onChange={(e) => setPicked(e.target.checked ? [...picked, ev] : picked.filter((x) => x !== ev))} />
                <code className="text-xs">{ev}</code>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => onClose()}>Cancel</Button>
          <Button type="submit" busy={save.isPending} disabled={!picked.length}>{sub ? 'Save' : 'Add webhook'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function Deliveries({ sub, onClose }: { sub: Sub; onClose: () => void }): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['webhook-deliveries', sub.id], queryFn: () => api<Delivery[]>(`/webhooks/${sub.id}/deliveries`) });
  const redeliver = useMutation({ mutationFn: (id: string) => api(`/webhooks/${sub.id}/deliveries/${id}/redeliver`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhook-deliveries', sub.id] }) });
  return (
    <Modal title="Deliveries" description={sub.url} onClose={onClose}>
      {q.isLoading ? <Skeleton className="h-32 w-full" /> : q.data?.length === 0 ? <p className="text-sm text-slate-500">Nothing sent yet.</p> : (
        <TableWrap>
          <thead><tr><Th>Event</Th><Th>Status</Th><Th>Last try</Th><Th> </Th></tr></thead>
          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {q.data?.map((d) => (
              <tr key={d.id}>
                <Td><code className="text-xs">{d.eventType}</code><span className="block text-xs text-slate-500">{dateTime(d.createdAt)}</span></Td>
                <Td><StatusChip tone={DELIVERY_TONE[d.status]}>{d.status.toLowerCase()}</StatusChip><span className="block text-xs text-slate-500">{d.attempts} tr{d.attempts === 1 ? 'y' : 'ies'}{d.lastStatusCode ? ` · HTTP ${d.lastStatusCode}` : ''}</span></Td>
                <Td className="max-w-[12rem] text-xs text-slate-500">{d.lastError ?? (d.deliveredAt ? `Delivered ${dateTime(d.deliveredAt)}` : `Next ${dateTime(d.nextAttemptAt)}`)}</Td>
                <Td>{can('integrations.manage') && d.status !== 'SUCCEEDED' && d.eventType !== 'webhook.test' && <Button size="sm" variant="secondary" busy={redeliver.isPending && redeliver.variables === d.id} onClick={() => redeliver.mutate(d.id)}>Redeliver</Button>}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Modal>
  );
}

export function WebhooksPage(): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['webhooks'], queryFn: () => api<Sub[]>('/webhooks') });
  const [editing, setEditing] = useState<Sub | 'new' | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Sub | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'critical'; text: string } | null>(null);
  const act = useMutation({
    mutationFn: ({ path, method = 'POST', body }: { path: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) => api<{ secret?: string; status?: string; lastStatusCode?: number | null; lastError?: string | null }>(path, { method, body }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
      if (r.secret) setSecret(r.secret);
      if (r.status) setNotice(r.status === 'SUCCEEDED' ? { tone: 'ok', text: `Test delivered (HTTP ${r.lastStatusCode}).` } : { tone: 'critical', text: `Test failed: ${r.lastError ?? 'no response'}.` });
    },
    onError: (e) => setNotice({ tone: 'critical', text: errorText(e) }),
  });
  const manage = can('integrations.manage');
  return (
    <>
      <PageHeader title="Webhooks" description="Tell other systems the moment something happens. Signed with HMAC-SHA256; retried with backoff for 24 hours." actions={manage && <Button onClick={() => setEditing('new')}><Plus aria-hidden="true" className="h-4 w-4" /> Add webhook</Button>} />
      {notice && <div className="mb-4"><Alert tone={notice.tone}>{notice.text}</Alert></div>}
      {list.isLoading && <Skeleton className="h-40 w-full" />}
      {list.data?.length === 0 && <Card><EmptyState title="No webhooks" description="Add an endpoint to receive invoice, receipt, payment, period and event notices." /></Card>}
      <div className="space-y-4">
        {list.data?.map((s) => (
          <Card
            key={s.id}
            title={s.url}
            description={s.disabledAt ? `Disabled automatically ${dateTime(s.disabledAt)} after ${s.failureCount} failures` : `${s.deliveries.SUCCEEDED ?? 0} delivered · ${(s.deliveries.FAILED ?? 0) + (s.deliveries.PENDING ?? 0)} pending · ${s.deliveries.DEAD ?? 0} gave up`}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip tone={s.active ? 'ok' : 'critical'}>{s.active ? 'active' : 'disabled'}</StatusChip>
                <Button size="sm" variant="secondary" onClick={() => setViewing(s)}>Deliveries</Button>
                {manage && <Button size="sm" variant="secondary" busy={act.isPending} onClick={() => act.mutate({ path: `/webhooks/${s.id}/test` })}><Send aria-hidden="true" className="h-4 w-4" /> Test</Button>}
                {manage && <Button size="sm" variant="secondary" onClick={() => setEditing(s)}>Edit</Button>}
                {manage && <Button size="sm" variant="secondary" onClick={() => act.mutate({ path: `/webhooks/${s.id}/rotate` })}>Rotate secret</Button>}
                {manage && <Button size="sm" variant={s.active ? 'ghost' : 'primary'} onClick={() => act.mutate({ path: `/webhooks/${s.id}`, method: 'PATCH', body: { active: !s.active } })}>{s.active ? 'Pause' : 'Resume'}</Button>}
                {manage && <Button size="sm" variant="danger" onClick={() => { if (window.confirm('Delete this webhook and its delivery log?')) act.mutate({ path: `/webhooks/${s.id}`, method: 'DELETE' }); }}>Delete</Button>}
              </div>
            }
          >
            <div className="flex flex-wrap gap-1">{s.events.map((e) => <code key={e} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">{e}</code>)}</div>
          </Card>
        ))}
      </div>
      <Card title="Verifying a delivery" className="mt-4">
        <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">Each POST carries <code className="text-xs">CoreX-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code>. Compute HMAC-SHA256 of <code className="text-xs">t + "." + body</code> with your secret, compare in constant time, and reject anything older than 5 minutes.</p>
        <JsonBlock value={{ id: 'uuid', type: 'receipt.posted', occurred_at: '2026-09-28T09:12:44.120Z', company_id: 'uuid', resource: { type: 'receipt', id: 'uuid', url: `${window.location.origin}/api/v1/receipts/uuid` } }} />
      </Card>
      {editing && <SubForm sub={editing === 'new' ? undefined : editing} onClose={(s) => { setEditing(null); if (s) setSecret(s); }} />}
      {secret && <SecretReveal title="Signing secret" items={[{ label: 'Secret', value: secret }]} onClose={() => setSecret(null)} />}
      {viewing && <Deliveries sub={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
